import { BIDC_MESSAGE_TYPES } from "@/constants/bidc-message-types";
import {
  isPhpToReactShortcutCommand,
  isPhpToReactShortcutNamespace,
  isReactToPhpShortcutCommand,
  isShortcutCommandResult,
} from "./shortcut-command-guards";
import type {
  PhpToReactShortcutCommand,
  ReactToPhpShortcutCommand,
  ShortcutCommandResult,
} from "./shortcut-command-types";

type ShortcutTransport = {
  send(message: {
    type: typeof BIDC_MESSAGE_TYPES.SHORTCUT_COMMAND;
    command: ReactToPhpShortcutCommand;
  }): Promise<unknown>;
};

type ShortcutExecutor = (
  command: PhpToReactShortcutCommand,
) => Promise<boolean>;

type ShortcutCommandBridgeOptions = {
  namespace: string;
  getTransport: () => ShortcutTransport | null;
  execute: ShortcutExecutor;
};

export type ShortcutCommandBridge = {
  receiveFromPhp(message: unknown): Promise<ShortcutCommandResult>;
  sendToPhp(command: ReactToPhpShortcutCommand): Promise<ShortcutCommandResult>;
};

function getCommand(message: unknown): unknown {
  if (
    typeof message !== "object" ||
    message === null ||
    !("command" in message)
  ) {
    return undefined;
  }

  return message.command;
}

export function createShortcutCommandBridge({
  namespace,
  getTransport,
  execute,
}: ShortcutCommandBridgeOptions): ShortcutCommandBridge {
  return {
    async receiveFromPhp(message) {
      if (!isPhpToReactShortcutNamespace(namespace)) {
        return { success: false, error: "unsupported_namespace" };
      }

      const command = getCommand(message);
      if (!isPhpToReactShortcutCommand(command)) {
        return { success: false, error: "unknown_command" };
      }

      try {
        return (await execute(command))
          ? { success: true }
          : { success: false, error: "missing_handler" };
      } catch {
        return { success: false, error: "handler_failed" };
      }
    },

    async sendToPhp(command) {
      if (!isReactToPhpShortcutCommand(command)) {
        return { success: false, error: "unknown_command" };
      }

      const transport = getTransport();
      if (!transport) {
        return { success: false, error: "channel_unavailable" };
      }

      try {
        const response = await transport.send({
          type: BIDC_MESSAGE_TYPES.SHORTCUT_COMMAND,
          command,
        });
        return isShortcutCommandResult(response)
          ? response
          : { success: false, error: "invalid_response" };
      } catch {
        return { success: false, error: "send_failed" };
      }
    },
  };
}
